import React, { useEffect, useMemo, useRef, useState } from "react";
import { updateRefClick } from "../../../../Utils/SupabaseServices/ContentDatabase";
import { addHttpToLink } from "../../../../Utils/StayUpToDate/RenderComponent";
import { useTemplateAnalyticsOptional } from "../TemplateAnalyticsContext";
import { TEMPLATE_ANALYTICS_EVENTS } from "../../../../Utils/SupabaseServices/templateAnalyticsEvents";
import { TEMPLATE_TRACKING_TYPES } from "../../../../Utils/SupabaseServices/TrackTemplate";
import type { ThemeTokens } from "../types";
import {
  shopContentCardLabel,
  type ShopCatalogContent,
} from "../templateShopCatalog";
import { TemplateTabFeedPendingGrid } from "../TemplateTabFeedPendingGrid";
import {
  CatalogEagerMediaContext,
  TemplateRevealImage,
  TemplateRevealMediaShell,
} from "../TemplateRevealImage";
import { TemplateFeedMediaRevealRoot } from "../TemplateFeedMediaRevealRoot";
import { CreatorFeedVideoPlayer } from "../../../../components/FeedPost/PlatformPosts/CreatorFeedVideoPlayer";
import { isTemplateCarouselSlideVideo } from "../templatePostMedia";
import { TemplatePostMediaCarousel } from "../TemplatePostMediaCarousel";
import {
  findTemplateBatchAnchor,
  scheduleScrollTemplateNewBatchIntoView,
} from "../scrollTemplateNewBatchIntoView";
import {
  TemplateMasonryGrid,
  useRiseAfterCatalogLoader,
} from "../TemplateMasonryGrid";
import {
  getBudgetedTemplateImageSrc,
  preloadResolvedImageSrcs,
} from "../../../../Utils/templateImageDelivery";
import { resolveFeedCardImage } from "../../../../Utils/media/feedCardImage";
import { isLikelyVideoUrl } from "../../../../Utils/media/feedMedia";
import {
  contentMediaRatio,
  contentPackWeight,
  safeMediaRatio,
} from "../templateMasonry";
import { ShopCatalogEmpty } from "../ShopBrowseNav";
import { resolveFeedScrollRoot } from "../../../../Utils/feedScrollRoot";
import { useEdit, E } from "../InlineEdit";
import "../templateMediaReveal.css";
import "../../../../components/CreatorFeed/CreatorFeed.css";

/** The content grid rises once per page visit, including after a route remount. */
let contentCatalogIntroSeen = false;

/** Every still the next Content/Search batch will actually paint. */
export function collectContentCatalogImageSrcs(item: ShopCatalogContent): string[] {
  const raws: string[] = [];
  const add = (value?: string | null) => {
    const src = String(value || "").trim();
    if (!src || src.includes("dummy_video") || isLikelyVideoUrl(src)) return;
    raws.push(src);
  };
  add(item.image);
  item.thumbnailUrls?.forEach(add);
  item.mediaUrls?.forEach(add);
  if (!item.isVideo) add(item.mediaUrl);
  const hrefs: string[] = [];
  for (const raw of raws) {
    hrefs.push(getBudgetedTemplateImageSrc(raw, "shop"));
    hrefs.push(resolveFeedCardImage(raw, true).src);
  }
  return hrefs;
}

const SHOP_INITIAL_BATCH = 25;
const SHOP_CHUNK_SIZE = 25;
const SHOP_MOBILE_INITIAL_BATCH = 10;

type ShopContentCatalogPanelProps = {
  T: ThemeTokens;
  items: ShopCatalogContent[];
  loading: boolean;
  userId: string;
  username?: string;
  cols: number;
  aspect: string;
  buttonLabel: string;
  catalogKey?: string;
  /** Search: parent concatenates pages — don't window or scroll-jump tiles. */
  disableInnerPagination?: boolean;
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  /** Filter change with previous tiles still on screen. */
  refreshing?: boolean;
};

type MasonryContent = ShopCatalogContent & {
  key: string;
  weight: number;
  ratio: number | null;
};

/**
 * Bottom-left control on video cards. At rest it's a play triangle; on
 * hover the card is already playing, so it becomes the audio toggle.
 */
function PlaybackButton({
  playing,
  muted,
  onPlay,
  onToggleMute,
}: {
  playing: boolean;
  muted: boolean;
  onPlay: () => void;
  onToggleMute: () => void;
}) {
  return (
    <button
      type="button"
      className={`mte-tpl-shop-playback${playing ? " is-playing" : ""}${
        !muted ? " is-unmuted" : ""
      }`}
      aria-label={playing ? (muted ? "Unmute" : "Mute") : "Play video"}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!playing) {
          onPlay();
          return;
        }
        // Let the controlled React mute state drive the <video> — mutating the
        // DOM here raced HoverVideoPlayer and made unmute feel broken.
        onToggleMute();
      }}
    >
      <span className="mte-tpl-shop-play" aria-hidden>
        <svg width="11" height="12" viewBox="0 0 16 18" fill="#fff">
          <polygon points="1,1 15,9 1,17" />
        </svg>
      </span>
      <span className="mte-tpl-shop-volume" aria-hidden>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
          <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="#fff" stroke="none" />
          {muted ? (
            <>
              <line x1="16" y1="9" x2="22" y2="15" strokeLinecap="round" />
              <line x1="22" y1="9" x2="16" y2="15" strokeLinecap="round" />
            </>
          ) : (
            <>
              <path d="M15.5 8.5a5 5 0 010 7" strokeLinecap="round" />
              <path d="M18.5 5.5a9 9 0 010 13" strokeLinecap="round" />
            </>
          )}
        </svg>
      </span>
    </button>
  );
}

function CardMenu({
  permalink,
  inline,
  onGoToContent,
}: {
  permalink: string;
  inline?: boolean;
  onGoToContent?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const href = addHttpToLink(String(permalink || "").trim());

  useEffect(() => {
    if (open) {
      setMounted(true);
      return;
    }
    if (!mounted) return;
    const t = window.setTimeout(() => setMounted(false), 220);
    return () => window.clearTimeout(t);
  }, [open, mounted]);

  useEffect(() => {
    if (!open) return;
    window.dispatchEvent(new Event("mte-we-exclusive-open"));
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onOther = () => setOpen(false);
    document.addEventListener("mousedown", onDown);
    window.addEventListener("mte-we-exclusive-open", onOther);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("mte-we-exclusive-open", onOther);
    };
  }, [open]);

  if (!href) return null;

  return (
    <div
      ref={ref}
      className={
        inline
          ? "mte-tpl-shop-dots mte-tpl-shop-dots--inline"
          : "mte-tpl-shop-dots"
      }
    >
      <button
        type="button"
        className="mte-tpl-shop-dots-btn"
        aria-label="More options"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <svg width="16" height="4" viewBox="0 0 16 4" fill="currentColor" aria-hidden>
          <circle cx="2" cy="2" r="1.6" />
          <circle cx="8" cy="2" r="1.6" />
          <circle cx="14" cy="2" r="1.6" />
        </svg>
      </button>
      {mounted ? (
        <div className={`mte-tpl-shop-dots-menu${open ? "" : " is-closing"}`}>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="mte-tpl-shop-dots-item"
            onClick={(e) => {
              e.stopPropagation();
              onGoToContent?.();
            }}
          >
            Go to content
          </a>
        </div>
      ) : null}
    </div>
  );
}

/** Shared content tile — used by Content page and mixed Search masonry. */
export const ShopContentCatalogCard = React.memo(function ShopContentCatalogCard({
  item,
  index,
  isMobile,
  buttonLabel,
  username,
  onShopClick,
  onGoToContent,
  ratio,
}: {
  item: ShopCatalogContent;
  index: number;
  isMobile: boolean;
  buttonLabel: string;
  username?: string;
  onShopClick: () => void;
  onGoToContent?: () => void;
  ratio?: number | null;
}) {
  const { editable } = useEdit();
  const shopHref = item.shoppable
    ? addHttpToLink(item.shopUrl) || item.permalink || "#"
    : item.permalink || addHttpToLink(item.shopUrl) || "#";
  const canOpen = Boolean(shopHref && shopHref !== "#" && !editable);
  const canShop = Boolean(
    item.shoppable && item.shopUrl && item.ctaLabel === "Shop",
  );
  const resolvedButtonLabel = item.ctaLabel || buttonLabel;
  const mediaSlides = item.mediaUrls;
  const isCarousel = Array.isArray(mediaSlides) && mediaSlides.length > 1;
  const [activeSlide, setActiveSlide] = useState(0);
  const playVideo = item.isVideo && Boolean(item.mediaUrl);
  const activeSlideIsVideo =
    isCarousel &&
    isTemplateCarouselSlideVideo(
      mediaSlides[activeSlide] || "",
      item.mediaUrl,
    );

  const isYouTube =
    String(item.platformSlug || "").trim().toLowerCase() === "youtube";
  // Content → YouTube: clean thumbnail only (no play / mute overlays).
  const showVideoChrome =
    (isCarousel ? activeSlideIsVideo : playVideo) && !isYouTube;
  const handle = username ? `@${username.replace(/^@/, "")}` : "";
  const format = shopContentCardLabel(item);
  const [muted, setMuted] = useState(true);
  const [hovered, setHovered] = useState(false);
  const [manualPlaying, setManualPlaying] = useState(false);
  const [allowVideo, setAllowVideo] = useState(false);
  const mediaWrapRef = useRef<HTMLDivElement | null>(null);
  const mediaRatio = ratio == null ? null : safeMediaRatio(ratio);
  const videoPlaying = showVideoChrome && (isMobile ? manualPlaying : hovered);

  // Don't mount HoverVideoPlayer for every tile. iPhone caps decoders;
  // 25 paused <video> nodes on first paint stalls /content.
  useEffect(() => {
    const needsVideo =
      playVideo ||
      (Array.isArray(mediaSlides) &&
        mediaSlides.some((url) =>
          isTemplateCarouselSlideVideo(url, item.mediaUrl),
        ));
    if (!needsVideo) return;
    if (isMobile) return;
    const el = mediaWrapRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setAllowVideo(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setAllowVideo(true);
          io.disconnect();
        }
      },
      { rootMargin: "120px 0px", threshold: 0.01 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [playVideo, item.mediaUrl, mediaSlides, isMobile]);

  const showPlayer = playVideo && (isMobile ? manualPlaying : allowVideo);
  const showShopPill = canShop;
  const shopPill = showShopPill ? (
    <span
      className="mte-tpl-shop-pill mte-tpl-shop-pill--mobile"
    >
      {resolvedButtonLabel.toUpperCase()}
    </span>
  ) : null;

  const openProps = canOpen
    ? {
        href: shopHref,
        target: "_blank" as const,
        rel: "noopener noreferrer",
        onClick: onShopClick,
      }
    : {
        href: undefined as string | undefined,
        onClick: (e: React.MouseEvent) => {
          e.preventDefault();
        },
      };

  if (item.isText) {
    const isLinkedIn = item.platformSlug === "linkedin";
    return (
      <div
        className="mte-tpl-shop-card"
        style={
          {
            ["--mte-tile-stagger" as string]: String(Math.min(index, 14)),
          } as React.CSSProperties
        }
      >
        <a
          className="mte-tpl-shop-text-card"
          {...(openProps as object)}
        >
          {isLinkedIn ? <div className="mte-tpl-shop-text-accent" aria-hidden /> : null}
          <div className="mte-tpl-shop-text-body">
            <div className="mte-tpl-shop-text-head">
              <span className="mte-tpl-shop-text-avatar">
                {isLinkedIn ? "in" : "X"}
              </span>
              {handle ? <span className="mte-tpl-shop-text-handle">{handle}</span> : null}
              {item.dateLabel ? (
                <span className="mte-tpl-shop-text-date">{item.dateLabel}</span>
              ) : null}
            </div>
            {item.caption ? (
              <div
                className={`mte-tpl-shop-text-copy${isLinkedIn ? " is-long" : ""}`}
                title={item.caption}
              >
                <E path={`content.${item.postId}.caption`} multiline>
                  {item.caption}
                </E>
              </div>
            ) : null}
          </div>
          {shopPill}
        </a>
        <div className="mte-tpl-shop-card-footer">
          <div className="mte-tpl-prod-cat mte-tpl-shop-content-format">{format}</div>
          <CardMenu
            permalink={
              item.permalink || (!item.shoppable ? item.shopUrl : "") || ""
            }
            inline
            onGoToContent={onGoToContent}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className="mte-tpl-shop-card"
      style={
        {
          ["--mte-tile-stagger" as string]: String(Math.min(index, 14)),
          ...(mediaRatio != null
            ? { ["--tpl-card-aspect" as string]: String(mediaRatio) }
            : {}),
        } as React.CSSProperties
      }
    >
      <div
        ref={mediaWrapRef}
        className={`mte-tpl-shop-card-media-wrap${hovered ? " is-hovered" : ""}`}
        onMouseEnter={() => {
          if (!isMobile) setHovered(true);
        }}
        onMouseLeave={() => {
          setHovered(false);
          if (!isMobile) setManualPlaying(false);
        }}
      >
        <a
          className="mte-tpl-shop-card-link"
          {...(openProps as object)}
        >
          <div className="mte-tpl-shop-card-frame">
            {isCarousel && mediaSlides ? (
              <TemplateRevealMediaShell
                posterSrc={
                  item.thumbnailUrls?.[0] ||
                  item.image ||
                  "/assets/dummy_video.png"
                }
                index={index}
                priority={index < (isMobile ? 6 : 12)}
                deliveryRole="shop"
              >
                <TemplatePostMediaCarousel
                  slides={mediaSlides}
                  posters={item.thumbnailUrls}
                  fallbackPoster={item.image || "/assets/dummy_video.png"}
                  allowVideo={isMobile ? manualPlaying : allowVideo}
                  isSoundOn={!isYouTube}
                  hideHoverSound
                  hidePlayButton
                  muted={muted}
                  onMutedChange={setMuted}
                  focused={videoPlaying}
                  playableVideoUrl={item.mediaUrl}
                  onSlideChange={setActiveSlide}
                  imageClassName="mte-tpl-img-filter mte-tpl-shop-card-img"
                  videoClassName="creator-feed-card-video hover-play mte-tpl-shop-hover-video"
                />
              </TemplateRevealMediaShell>
            ) : showPlayer ? (
              <TemplateRevealMediaShell
                posterSrc={item.image || "/assets/dummy_video.png"}
                index={index}
                priority={index < (isMobile ? 6 : 12)}
                deliveryRole="shop"
              >
                <CreatorFeedVideoPlayer
                  media_url={item.mediaUrl}
                  thumbnail={item.image || "/assets/dummy_video.png"}
                  isSoundOn={!isYouTube}
                  hideHoverSound
                  hidePlayButton
                  muted={muted}
                  onMutedChange={setMuted}
                  focused={videoPlaying}
                  hoverTarget={mediaWrapRef}
                  className="creator-feed-card-video hover-play mte-tpl-shop-hover-video"
                />
              </TemplateRevealMediaShell>
            ) : item.image || playVideo ? (
              <TemplateRevealImage
                src={item.image || "/assets/dummy_video.png"}
                alt=""
                index={index}
                priority={index < (isMobile ? 6 : 12)}
                deliveryRole="shop"
                className="mte-tpl-img-filter mte-tpl-shop-card-img"
              />
            ) : null}
            {showShopPill ? shopPill : null}
          </div>
        </a>
        {showVideoChrome ? (
          <PlaybackButton
            playing={videoPlaying}
            muted={muted}
            onPlay={() => setManualPlaying(true)}
            onToggleMute={() => setMuted((m) => !m)}
          />
        ) : null}
        <div className="mte-tpl-shop-media-tools">
          <CardMenu
            permalink={
              item.permalink || (!item.shoppable ? item.shopUrl : "") || ""
            }
            onGoToContent={onGoToContent}
          />
        </div>
      </div>
      {item.caption ? (
        <div className="mte-tpl-shop-content-meta">
          <div className="mte-tpl-shop-content-caption" title={item.caption}>
            <E path={`content.${item.postId}.caption`} multiline>
              {item.caption}
            </E>
          </div>
        </div>
      ) : null}
    </div>
  );
});

export function ShopContentCatalogPanel({
  T,
  items,
  loading,
  userId,
  username,
  cols,
  aspect: _aspect,
  buttonLabel,
  catalogKey,
  /** Search: parent concatenates pages — don't window or scroll-jump tiles. */
  disableInnerPagination = false,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  refreshing = false,
}: ShopContentCatalogPanelProps) {
  void _aspect;
  const templateAnalytics = useTemplateAnalyticsOptional();
  const isMobile = T.device === "mobile";
  const networkPaging = Boolean(onLoadMore);
  const initialBatch = isMobile ? SHOP_MOBILE_INITIAL_BATCH : SHOP_INITIAL_BATCH;

  const [visibleCount, setVisibleCount] = useState(initialBatch);
  const [isRevealingBatch, setIsRevealingBatch] = useState(false);
  const [playEnterAnim, setPlayEnterAnim] = useState(false);
  const [appliedCatalogKey, setAppliedCatalogKey] = useState(catalogKey);
  const [arriving, setArriving] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const isLoadingRef = useRef(false);
  const itemsLenRef = useRef(items.length);
  const hasMoreRef = useRef(hasMore);
  const loadingMoreRef = useRef(loadingMore);
  const onLoadMoreRef = useRef(onLoadMore);
  const prevResultsSigRef = useRef<string | null>(null);
  const heldSigRef = useRef<string | null>(null);
  const [riseHold, setRiseHold] = useState(() => contentCatalogIntroSeen);
  itemsLenRef.current = items.length;
  hasMoreRef.current = hasMore;
  loadingMoreRef.current = loadingMore;
  onLoadMoreRef.current = onLoadMore;

  const catalogNeedsApply = !refreshing && appliedCatalogKey !== catalogKey;
  if (catalogNeedsApply) {
    setAppliedCatalogKey(catalogKey);
    setVisibleCount(initialBatch);
    setIsRevealingBatch(false);
    isLoadingRef.current = false;
    if (prevResultsSigRef.current !== null && !riseHold) {
      heldSigRef.current = prevResultsSigRef.current;
      setRiseHold(true);
    }
  }
  const shownCount = catalogNeedsApply ? initialBatch : visibleCount;

  const resultsSig = items
    .slice(0, 8)
    .map((p) => p.postId)
    .join("\0");
  let arriveThisPaint = arriving;
  if (!loading && !refreshing) {
    if (prevResultsSigRef.current === null) {
      prevResultsSigRef.current = resultsSig;
      if (resultsSig && !contentCatalogIntroSeen) {
        contentCatalogIntroSeen = true;
        arriveThisPaint = true;
        setArriving(true);
        setPlayEnterAnim(true);
      }
    } else if (resultsSig !== prevResultsSigRef.current) {
      prevResultsSigRef.current = resultsSig;
    }
  }

  useEffect(() => {
    if (!arriving) return;
    setPlayEnterAnim(true);
    const t = window.setTimeout(() => {
      setArriving(false);
      setPlayEnterAnim(false);
    }, 960);
    return () => window.clearTimeout(t);
  }, [arriving]);

  useEffect(() => {
    if (!riseHold) return;
    if (!resultsSig || resultsSig === heldSigRef.current) return;
    const id = window.requestAnimationFrame(() => setRiseHold(false));
    return () => window.cancelAnimationFrame(id);
  }, [riseHold, resultsSig]);

  const showingLoader = (loading || refreshing) && items.length === 0;
  const riseAfterLoader = useRiseAfterCatalogLoader(
    showingLoader,
    items.length > 0,
  );

  // Keep one page ahead in memory. Do not paint it until the user scrolls.
  useEffect(() => {
    if (disableInnerPagination || !networkPaging || !hasMore || loadingMore) return;
    if (items.length === 0) return;
    if (items.length >= shownCount + SHOP_CHUNK_SIZE) return;
    onLoadMore?.();
  }, [
    disableInnerPagination,
    networkPaging,
    hasMore,
    loadingMore,
    items.length,
    shownCount,
    onLoadMore,
  ]);

  useEffect(() => {
    if (disableInnerPagination) return;
    const next = items.slice(shownCount, shownCount + SHOP_CHUNK_SIZE);
    preloadResolvedImageSrcs(next.flatMap(collectContentCatalogImageSrcs));
  }, [disableInnerPagination, items, shownCount]);

  const visibleItems = useMemo(
    () =>
      disableInnerPagination ? items : items.slice(0, shownCount),
    [items, shownCount, disableInnerPagination],
  );

  const masonryItems = useMemo<MasonryContent[]>(
    () =>
      visibleItems.map((item) => {
        const ratio = contentMediaRatio(item);
        // Instagram posts: alternate portrait / square for zigzag (artifact).
        let resolved = ratio;
        if (
          resolved != null &&
          item.platformSlug === "instagram" &&
          !item.isVideo &&
          item.formatSlug !== "reels" &&
          item.formatSlug !== "stories"
        ) {
          const odd =
            [...String(item.postId)].reduce((a, c) => a + c.charCodeAt(0), 0) %
              2 ===
            1;
          resolved = odd ? 4 / 5 : 1;
        }
        return {
          ...item,
          key: item.postId,
          ratio: resolved,
          weight: contentPackWeight(resolved, {
            isText: item.isText,
            hasCaption: Boolean(item.caption),
          }),
        };
      }),
    [visibleItems],
  );

  const impressedIdsRef = useRef(new Set<string>());
  const impressedCatalogKeyRef = useRef(catalogKey);
  useEffect(() => {
    if (impressedCatalogKeyRef.current !== catalogKey) {
      impressedCatalogKeyRef.current = catalogKey;
      impressedIdsRef.current = new Set();
    }
    if (!templateAnalytics?.enabled) return;
    if (templateAnalytics.pageKey !== "shop") return;
    const next = visibleItems.filter(
      (p) => p.postId && !impressedIdsRef.current.has(p.postId),
    );
    if (next.length === 0) return;
    next.forEach((p) => impressedIdsRef.current.add(p.postId));
    templateAnalytics.trackSectionEvent(TEMPLATE_ANALYTICS_EVENTS.PRODUCT_IMPRESSION, {
      sectionId: "productGrid",
      sectionType: "ProductGrid",
      entityType: "post",
      eventType: TEMPLATE_TRACKING_TYPES.RENDER,
      details: {
        post_ids: next.map((p) => p.postId),
        batch_size: next.length,
        shop_view: "content",
      },
    });
  }, [visibleItems, templateAnalytics, catalogKey]);

  useEffect(() => {
    if (disableInnerPagination) return;
    if (observerRef.current) observerRef.current.disconnect();

    const hasRows = items.length > 0;
    const canRevealLocal = shownCount < items.length;
    const canFetchNetwork =
      networkPaging && hasMore && shownCount >= items.length;
    if (!hasRows || (!canRevealLocal && !canFetchNetwork)) return;

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const nested = resolveFeedScrollRoot();
    const scrollRoot = nested instanceof HTMLElement ? nested : null;

    let revealTimer = 0;
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        if (isLoadingRef.current) return;

        // Paint one already-fetched chunk. Extra pages stay in memory.
        if (shownCount < itemsLenRef.current) {
          isLoadingRef.current = true;
          setIsRevealingBatch(true);
          const batchStart = shownCount;
          revealTimer = window.setTimeout(() => {
            revealTimer = 0;
            setVisibleCount((prev) =>
              Math.min(prev + SHOP_CHUNK_SIZE, itemsLenRef.current),
            );
            setIsRevealingBatch(false);
            isLoadingRef.current = false;
            if (!isMobile) {
              scheduleScrollTemplateNewBatchIntoView(
                () => findTemplateBatchAnchor(gridRef.current, batchStart),
                { behavior: "auto" },
              );
            }
          }, 1100);
          return;
        }

        if (!hasMoreRef.current) return;
        isLoadingRef.current = true;
        setIsRevealingBatch(true);
        if (networkPaging && !loadingMoreRef.current) onLoadMoreRef.current?.();
        const started = Date.now();
        const batchStart = shownCount;
        const tick = () => {
          const ready = batchStart < itemsLenRef.current;
          const elapsed = Date.now() - started;
          if (ready && elapsed >= 1100) {
            revealTimer = 0;
            setVisibleCount((prev) =>
              Math.min(prev + SHOP_CHUNK_SIZE, itemsLenRef.current),
            );
            setIsRevealingBatch(false);
            isLoadingRef.current = false;
            if (!isMobile) {
              scheduleScrollTemplateNewBatchIntoView(
                () => findTemplateBatchAnchor(gridRef.current, batchStart),
                { behavior: "auto" },
              );
            }
            return;
          }
          if (
            !ready &&
            ((!hasMoreRef.current && !loadingMoreRef.current) || elapsed > 8000)
          ) {
            revealTimer = 0;
            setIsRevealingBatch(false);
            isLoadingRef.current = false;
            return;
          }
          revealTimer = window.setTimeout(tick, 80);
        };
        revealTimer = window.setTimeout(tick, 80);
      },
      {
        root: scrollRoot ?? null,
        rootMargin: isMobile ? "0px 0px" : "50px 0px",
        threshold: 0,
      },
    );
    observerRef.current.observe(sentinel);
    return () => {
      if (revealTimer) {
        window.clearTimeout(revealTimer);
        isLoadingRef.current = false;
        setIsRevealingBatch(false);
      }
      observerRef.current?.disconnect();
    };
  }, [
    shownCount,
    items.length > 0,
    hasMore,
    disableInnerPagination,
    networkPaging,
    isMobile,
  ]);

  // Grey tiles while a filter page is in flight — don't freeze the old photos.
  if ((loading || refreshing) && items.length === 0) {
    return (
      <div
        className="mte-tpl-tab-feed-stage mte-tpl-tab-feed-stage--shop mte-tpl-shop-catalog-skeleton"
        aria-busy="true"
        data-website-tour="shop-results"
      >
        <TemplateTabFeedPendingGrid pageVariant="shop" device={T.device} T={T} />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div data-website-tour="shop-results">
        <ShopCatalogEmpty message="No content to show in this view yet." />
      </div>
    );
  }

  // Search owns paging + outer `plainMedia` root — don't nest a reveal queue.
  const grid = (
      <>
        <TemplateMasonryGrid
          gridRef={gridRef}
          pending={!disableInnerPagination && isRevealingBatch}
          cellRise={!riseHold || riseAfterLoader}
          className={`mte-tpl-shop-grid mte-tpl-shop-grid--masonry mte-tpl-animate-tiles${playEnterAnim ? " mte-tpl-grid-enter" : ""}`}
          items={masonryItems}
          maxCols={Math.max(6, cols || 0)}
          device={T.device}
          renderItem={(item, index) => (
            <ShopContentCatalogCard
              item={item}
              index={index}
              isMobile={isMobile}
              buttonLabel={buttonLabel}
              username={username}
              ratio={item.ratio}
              onShopClick={() => {
                if (!userId?.trim() || !item.postId) return;
                const mediaUrl = item.mediaUrl || item.image;
                const name = item.caption || item.postId;
                const contentTab =
                  shopContentCardLabel(item) || item.platform || undefined;
                // Always count a Post Click so Content → Top Viewed is populated.
                templateAnalytics?.trackPostClick({
                  postId: item.postId,
                  name,
                  title: name,
                  sectionId: "contentGrid",
                  sectionType: "ContentGrid",
                  platform: item.platform,
                  mediaUrl,
                  contentView: "latest",
                  contentTab,
                });
                if (item.shoppable) {
                  templateAnalytics?.trackShopClick({
                    postId: item.postId,
                    name,
                    sectionId: "contentGrid",
                    shopView: "content",
                    platform: item.platform,
                    mediaUrl,
                  });
                } else {
                  templateAnalytics?.trackSectionEvent(
                    TEMPLATE_ANALYTICS_EVENTS.ORIGINAL_POST,
                    {
                      sectionId: "contentGrid",
                      sectionType: "ContentGrid",
                      entityType: "post",
                      entityId: item.postId,
                      entityLabel: name,
                      details: {
                        post_id: item.postId,
                        media_url: mediaUrl,
                        platform: item.platform,
                        name,
                        content_view: "latest",
                        content_tab: contentTab,
                      },
                    },
                  );
                }
                void updateRefClick(item.postId, userId);
              }}
              onGoToContent={() => {
                if (!userId?.trim() || !item.postId) return;
                const mediaUrl = item.mediaUrl || item.image;
                const name = item.caption || item.postId;
                const contentTab =
                  shopContentCardLabel(item) || item.platform || undefined;
                templateAnalytics?.trackPostClick({
                  postId: item.postId,
                  name,
                  title: name,
                  sectionId: "contentGrid",
                  sectionType: "ContentGrid",
                  platform: item.platform,
                  mediaUrl,
                  contentView: "latest",
                  contentTab,
                });
                templateAnalytics?.trackSectionEvent(
                  TEMPLATE_ANALYTICS_EVENTS.ORIGINAL_POST,
                  {
                    sectionId: "contentGrid",
                    sectionType: "ContentGrid",
                    entityType: "post",
                    entityId: item.postId,
                    entityLabel: name,
                    details: {
                      post_id: item.postId,
                      media_url: mediaUrl,
                      platform: item.platform,
                      name,
                      content_view: "latest",
                      content_tab: contentTab,
                      source: "go_to_content",
                    },
                  },
                );
                void updateRefClick(item.postId, userId);
              }}
            />
          )}
        />
        {((shownCount < items.length) ||
          (networkPaging && hasMore && shownCount >= items.length)) &&
          !disableInnerPagination && (
          <div ref={sentinelRef} aria-hidden style={{ height: 1, width: "100%" }} />
        )}
      </>
  );

  return (
    <div
      data-website-surface="shop-results"
      className={`mte-tpl-shop-catalog-stage${arriveThisPaint ? " is-arriving" : ""}`}
      aria-busy={refreshing || loading || undefined}
    >
      <div className="mte-tpl-shop-catalog-stage-grid">
        {disableInnerPagination ? (
          grid
        ) : (
          <CatalogEagerMediaContext.Provider value>
            <TemplateFeedMediaRevealRoot plainMedia>
              {grid}
            </TemplateFeedMediaRevealRoot>
          </CatalogEagerMediaContext.Provider>
        )}
      </div>
    </div>
  );
}
